import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Banner, Spinner } from './States';
import { useCurrency } from '../hooks/useCurrency';
import { cleanName } from '../lib/format';
import type { FeaturedAdminItem, SkinGroup } from '../api/types';

const MAX = 40;

/**
 * Curadoria da primeira página.
 *
 * Busca no catálogo, escolhe as skins e define a ordem. A lista já mostra o
 * preço atual de cada uma: destacar algo que sumiu do catálogo é o erro que
 * ninguém percebe até um cliente reclamar.
 */
export function FeaturedPanel() {
  const { money } = useCurrency();
  const [items, setItems] = useState<FeaturedAdminItem[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<SkinGroup[] | null>(null);
  const [buscando, setBuscando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const res = await api.adminFeatured();
      setItems(res.items);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar os destaques.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // A busca espera a digitação parar: uma consulta por tecla varreria o
  // catálogo inteiro a cada letra.
  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 3) {
      setResultados(null);
      return;
    }
    setBuscando(true);
    const timer = window.setTimeout(() => {
      void api
        .skins({ category: 'all', search: termo, pageSize: 24, sort: 'onsale' })
        .then((r) => setResultados(r.items))
        .catch(() => setResultados([]))
        .finally(() => setBuscando(false));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [busca]);

  async function adicionar(g: SkinGroup) {
    setOcupado(true);
    setErro(null);
    setOk(null);
    try {
      const res = await api.adminAddFeatured({
        baseKey: g.key,
        name: g.name,
        weaponCode: g.variants[0]?.weaponCode ?? null,
        kind: g.variants[0]?.kind ?? null,
      });
      setItems(res.items.map((f) => ({ ...f, iconUrl: null, minPrice: null, variantCount: 0, indisponivel: false })));
      setOk(`"${cleanName(g.name)}" entrou nos destaques.`);
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui adicionar.');
    } finally {
      setOcupado(false);
    }
  }

  async function remover(baseKey: string) {
    setOcupado(true);
    setErro(null);
    try {
      await api.adminRemoveFeatured(baseKey);
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui remover.');
    } finally {
      setOcupado(false);
    }
  }

  async function mover(indice: number, direcao: -1 | 1) {
    if (!items) return;
    const destino = indice + direcao;
    if (destino < 0 || destino >= items.length) return;

    const nova = [...items];
    [nova[indice], nova[destino]] = [nova[destino]!, nova[indice]!];
    setItems(nova); // otimista: a lista responde na hora
    try {
      await api.adminReorderFeatured(nova.map((f) => f.baseKey));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui reordenar.');
      await carregar();
    }
  }

  if (erro && !items) return <Banner kind="error">{erro}</Banner>;
  if (!items) return <Spinner label="Carregando os destaques…" />;

  const jaEscolhidas = new Set(items.map((f) => f.baseKey));
  const indisponiveis = items.filter((f) => f.indisponivel).length;

  return (
    <div className="panel">
      <h2>Destaques da primeira página</h2>
      <p className="muted">
        As skins que aparecem na home, na ordem definida aqui. O preço de cada card sai do catálogo do momento — a
        curadoria é só de <i>quais</i> skins, nunca de preço.
      </p>

      {erro && <Banner kind="error">{erro}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}
      {indisponiveis > 0 && (
        <Banner kind="warn">
          {indisponiveis === 1 ? '1 destaque não está' : `${indisponiveis} destaques não estão`} disponível(is) no
          catálogo agora — some(m) da home até voltar(em) a ter anúncio acima do piso de preço.
        </Banner>
      )}

      <div className="stat-row">
        <span>
          <b>{items.length}</b> de {MAX} destaques
        </span>
        <span className="dim">o ideal é entre 20 e 30</span>
      </div>

      {items.length === 0 ? (
        <p className="muted">Nenhum destaque ainda. Busque abaixo e adicione as primeiras.</p>
      ) : (
        <ol className="featured-list">
          {items.map((f, i) => (
            <li key={f.baseKey} className={f.indisponivel ? 'indisponivel' : ''}>
              <span className="pos">{i + 1}</span>
              {f.iconUrl ? <img src={f.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
              <div className="info">
                <b>{cleanName(f.name)}</b>
                <span className="dim">
                  {f.indisponivel
                    ? 'indisponível no catálogo'
                    : `${money(f.minPrice)} · ${f.variantCount} desgaste(s)`}
                </span>
              </div>
              <div className="actions">
                <button className="btn ghost small" onClick={() => void mover(i, -1)} disabled={i === 0 || ocupado}>
                  ↑
                </button>
                <button
                  className="btn ghost small"
                  onClick={() => void mover(i, 1)}
                  disabled={i === items.length - 1 || ocupado}
                >
                  ↓
                </button>
                <button className="btn ghost small" onClick={() => void remover(f.baseKey)} disabled={ocupado}>
                  Remover
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      <h3>Adicionar skin</h3>
      <div className="actions-row">
        <input
          placeholder="Busque pelo nome: AK-47, Karambit, Asiimov…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        {buscando && <span className="dim">buscando…</span>}
      </div>

      {resultados && resultados.length === 0 && !buscando && (
        <p className="muted">Nada encontrado para “{busca}”.</p>
      )}

      {resultados && resultados.length > 0 && (
        <div className="busca-grid">
          {resultados.map((g) => {
            const dentro = jaEscolhidas.has(g.key);
            return (
              <button
                key={g.key}
                className={`busca-card ${dentro ? 'dentro' : ''}`}
                onClick={() => !dentro && void adicionar(g)}
                disabled={dentro || ocupado || items.length >= MAX}
                title={dentro ? 'Já está nos destaques' : 'Adicionar aos destaques'}
              >
                {g.iconUrl ? <img src={g.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
                <b>{cleanName(g.name)}</b>
                <span className="dim">{money(g.minPrice)}</span>
                {dentro && <span className="tag">no destaque</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
